import type { GetWebcastStreamQuery, GetWebcastViewerQuery } from '../../../generated/graphql-viewer';

import { useEffect } from 'react';
import { useParams } from 'react-router';

import { LiveView } from './components/live-view/live-view';
import { PreAndPostLiveView } from './components/pre-and-post-live-view/pre-and-post-live-view';
import { VideoOnDemandView } from './components/video-on-demand-view/video-on-demand-view';
import { useDevTools } from '../../../dev-tools';
import {
  State,
  StreamState,
  useGetWebcastStreamQuery,
  useGetWebcastViewerQuery,
} from '../../../generated/graphql-viewer';
import { useSelectedLanguage } from '../../providers/selected-language-provider';
import { useViewerAccess } from '../../providers/viewer-access-provider';

export function Stream() {
  const { webcastId = '' } = useParams();
  const { mockedViewerState } = useDevTools();
  const { selectedLanguage } = useSelectedLanguage();
  const { email, accessCode, protectionToken } = useViewerAccess();

  const queryOptions = { variables: { webcastId, accessCode, email, protectionToken } };
  const { data, startPolling, stopPolling, refetch } = useGetWebcastViewerQuery(queryOptions);
  const {
    data: getWebcastStreamResponse,
    startPolling: getWebcastStreamResponseStartPolling,
    stopPolling: getWebcastStreamResponseStopPolling,
  } = useGetWebcastStreamQuery(queryOptions);

  const webcast = {
    ...data?.webcast,
    ...getWebcastStreamResponse?.webcast,
  } as GetWebcastStreamQuery['webcast'] & GetWebcastViewerQuery['webcast'];

  const webcastState = data?.webcast?.state;
  const webcastIsPreLive = webcastState === State.PRELIVE;
  const webcastIsLive = webcastState === State.LIVE || webcastState === State.LIVE_TEST;
  const webcastIsPostLive = webcastState === State.POSTLIVE;
  const vodEnabled = getWebcastStreamResponse?.webcast?.streaming?.vod?.enabled;
  const streams = getWebcastStreamResponse?.webcast?.streaming?.live?.streams;
  const currentStream = streams?.find((stream) => stream.metadata?.language === selectedLanguage?.code) || streams?.[0];
  const primaryStreamConnected = currentStream?.primaryStatus?.streamState === StreamState.CONNECTED;
  const backupStreamConnected = currentStream?.backupStatus?.streamState === StreamState.CONNECTED;

  useEffect(() => {
    startPolling(5000);
    return stopPolling;
  }, [startPolling, stopPolling]);

  useEffect(() => {
    if (primaryStreamConnected || backupStreamConnected) {
      getWebcastStreamResponseStopPolling();
      return;
    }

    getWebcastStreamResponseStartPolling(5000);
    return getWebcastStreamResponseStopPolling;
  }, [
    backupStreamConnected,
    getWebcastStreamResponseStartPolling,
    getWebcastStreamResponseStopPolling,
    primaryStreamConnected,
  ]);

  useEffect(() => {
    if (email || accessCode) refetch();
  }, [accessCode, email, refetch]);

  if (!webcastState) return null;

  if (mockedViewerState) {
    if (mockedViewerState === 'live') return <LiveView webcast={webcast} />;
    if (mockedViewerState === 'on-demand') return <VideoOnDemandView webcast={webcast} />;
    if (mockedViewerState === 'pre-post') return <PreAndPostLiveView webcast={webcast} />;
  }

  if (webcastIsLive) return <LiveView webcast={webcast} />;
  if (vodEnabled) return <VideoOnDemandView webcast={webcast} />;
  if (webcastIsPreLive || webcastIsPostLive) return <PreAndPostLiveView webcast={webcast} />;

  return null;
}
